import CheckboxGroupElement from './CheckboxGroupElement.vue'

import type { CheckboxGroupDirection, CheckboxGroupElementInput, FlashcardSize } from '../types'
import type { ElementDefinition } from '../runtime/elementDefinition'
import { scaleWidth } from '../runtime/math'

const normalizeCount = (value?: number, fallback = 0) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return Math.max(0, fallback)
  }
  return Math.max(0, Math.floor(value))
}

const normalizeCheckboxValues = (input: CheckboxGroupElementInput): boolean[] => {
  const provided = Array.isArray(input.values) ? input.values.map(Boolean) : []
  const requested = normalizeCount(input.count, provided.length)
  const total = Math.max(provided.length, requested)

  if (total === 0) {
    return []
  }

  return Array.from({ length: total }, (_, index) => Boolean(provided[index]))
}

const estimateCheckboxGroupBox = (input: CheckboxGroupElementInput, size: FlashcardSize) => {
  const values = normalizeCheckboxValues(input)
  const direction: CheckboxGroupDirection =
    input.direction === 'horizontal' ? 'horizontal' : 'vertical'
  const gapValue = typeof input.gap === 'number' ? input.gap : 12
  const sizeValue = typeof input.size === 'number' ? input.size : 18
  const gapPx = scaleWidth(gapValue, size)
  const boxPx = scaleWidth(sizeValue, size)
  const count = Math.max(1, values.length || normalizeCount(input.count, 1) || 1)
  if (!Number.isFinite(gapPx) || !Number.isFinite(boxPx)) return null

  if (direction === 'horizontal') {
    return {
      widthPx: boxPx * count + gapPx * (count - 1),
      heightPx: boxPx,
    }
  }
  return {
    widthPx: boxPx,
    heightPx: boxPx * count + gapPx * (count - 1),
  }
}

const definition: ElementDefinition<CheckboxGroupElementInput> = {
  type: 'checkbox-group',
  component: CheckboxGroupElement,
  estimateBox: (input, size) => estimateCheckboxGroupBox(input, size),
  toProps: (input, ctx) => {
    let values = normalizeCheckboxValues(input)
    const isReadOnly =
      typeof input.stateKey === 'string' && input.stateKey.startsWith('pfc:readonly:')
    const direction: CheckboxGroupDirection =
      input.direction === 'horizontal' ? 'horizontal' : 'vertical'

    const gapValue = typeof input.gap === 'number' ? input.gap : 12
    const sizeValue = typeof input.size === 'number' ? input.size : 18
    const defaultBackground = ctx.theme.darkMode
      ? ctx.theme.backgroundColor
      : 'rgba(255, 255, 255, 1)'
    const backgroundColor =
      typeof input.backgroundColor === 'string' ? input.backgroundColor : defaultBackground

    const gapPx = scaleWidth(gapValue, ctx.size)
    const boxSizePx = scaleWidth(sizeValue, ctx.size)

    if (!Number.isFinite(gapPx) || !Number.isFinite(boxSizePx)) {
      return null
    }

    const items = values.map((checked, index) => ({
      id: `${input.stateKey ?? ctx.context.key}-${index}`,
      checked,
    }))

    const handleToggle = (index: number, nextValue: boolean, nextValues?: boolean[]) => {
      const resolvedValues = Array.isArray(nextValues)
        ? nextValues.map(Boolean)
        : values.map((value, valueIndex) => (valueIndex === index ? nextValue : value))

      values = resolvedValues

      ctx.context.emitElementEvent?.({
        face: ctx.context.face,
        elementKey: ctx.context.key,
        elementIndex: ctx.context.index,
        elementType: 'checkbox-group',
        detail: {
          index,
          checked: nextValue,
          values: resolvedValues,
          stateKey: input.stateKey,
        },
      })
    }

    return {
      style: ctx.baseStyle,
      items,
      direction,
      gap: gapPx,
      boxSize: boxSizePx,
      tickStyle: input.tickStyle,
      themeDark: ctx.theme.darkMode,
      backgroundColor,
      interactive: !isReadOnly,
      onToggle: handleToggle,
    }
  },
}

export default definition
